import { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { BootResponse, Preview } from '@appcharge/shared-ui';
import useOffers from 'api/useOffers';
import useOffersUI from 'api/useOffersUI';
import { useProduct } from 'api/useProduct';
import useTheme from 'api/useTheme';
import { Offer, OfferBadge } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { TRANSLATE_MOCK_MAP } from 'views/Promotions/constants';
import { z } from 'zod';

import { Product } from 'components/ProductsForm/ProductsForm.types';

import { templateStudioSchema } from '../validation-schema';

import { generateBootInfo } from './utils/boot';
import { generateOffersAPIResponse } from './utils/offers';
import { generatePlayerData } from './utils/playerData';

export const usePreview = () => {
  const form = useFormContext<z.infer<typeof templateStudioSchema>>();
  const {
    previewDeviceLayout,
    displayPopupPreview,
    setDisplayPopupPreview,
    scrollToSection
  } = useTemplateStudioState();
  const { watch } = form;
  const { publisherId } = useParams();
  const { getStoreTheme } = useTheme(publisherId);
  const { getProducts } = useProduct(publisherId!);
  const { getOffersUI } = useOffersUI(publisherId!);
  const { getOffers: getPromotions } = useOffers({
    offerType: OfferType.SPECIAL_OFFER,
    fetchOnlyActiveOffers: true,
    shouldIgnorePagination: true
  });

  const formValues = watch();

  const boot: BootResponse = useMemo(() => {
    if (!getStoreTheme.data) return;

    return generateBootInfo(
      formValues,
      getStoreTheme.data,
      previewDeviceLayout
    );
  }, [formValues, getStoreTheme.data, previewDeviceLayout]);

  const offers = useMemo(() => {
    if (!boot) return;

    const realPromotionBadges: OfferBadge[] =
      getPromotions?.data?.offers?.flatMap(
        (offer: Offer) => offer.badges || []
      ) || [];

    const products: Product[] = getProducts.data?.result
      ? [...getProducts.data?.result]
      : [];

    // Ensure products array has at least 10 items by duplicating the last product
    if (products.length > 0 && products.length < 10) {
      const lastProduct = products[products.length - 1];
      while (products.length < 10) {
        products.push({ ...lastProduct });
      }
    }

    return generateOffersAPIResponse({
      formValues,
      boot,
      products: products,
      offerUIS: getOffersUI.data?.result || [],
      promotionBadges: realPromotionBadges
    });
  }, [
    formValues,
    boot?.storeTheme?.general?.productsQuantityFormat?.milSeparator,
    boot?.storeTheme?.general?.productsQuantityFormat?.fractionalSeparator,
    getProducts.data?.result,
    getOffersUI.data?.result,
    getPromotions?.data?.offers
  ]);

  const previewProps: React.ComponentProps<typeof Preview> = useMemo(
    () => ({
      boot,
      playerData: generatePlayerData(),
      device: previewDeviceLayout,
      translate: (key: string) => TRANSLATE_MOCK_MAP[key] || key,
      offers,
      displayPopup: displayPopupPreview,
      onClosePopup: () => setDisplayPopupPreview(false),
      scrollToSection
    }),
    [
      boot,
      offers,
      previewDeviceLayout,
      displayPopupPreview,
      setDisplayPopupPreview,
      scrollToSection
    ]
  );

  return { previewProps };
};
